import { createContext, useContext, useState, type ReactNode } from 'react'
import { startOfWeek } from 'date-fns'
import { useSettings } from './settings-context'

interface WeekContextType {
  currentWeekStart: Date
  setCurrentWeekStart: (date: Date) => void
  goToPreviousWeek: () => void
  goToNextWeek: () => void
}

const WeekContext = createContext<WeekContextType | undefined>(undefined)

interface WeekProviderProps {
  children: ReactNode
}

export function WeekProvider({ children }: WeekProviderProps) {
  const { weekStartsOn } = useSettings()
  const weekStartsOnNumber = weekStartsOn === 'monday' ? 1 : 0
  
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: weekStartsOnNumber })
  )

  const goToPreviousWeek = () => {
    const previousWeek = new Date(currentWeekStart)
    previousWeek.setDate(previousWeek.getDate() - 7)
    setCurrentWeekStart(startOfWeek(previousWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStart)
    nextWeek.setDate(nextWeek.getDate() + 7)
    setCurrentWeekStart(startOfWeek(nextWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const value: WeekContextType = {
    currentWeekStart,
    setCurrentWeekStart,
    goToPreviousWeek,
    goToNextWeek,
  }

  return (
    <WeekContext.Provider value={value}>
      {children}
    </WeekContext.Provider>
  )
}

export function useWeek() {
  const context = useContext(WeekContext)
  if (context === undefined) {
    throw new Error('useWeek must be used within a WeekProvider')
  }
  return context
}
